import React, { useState } from 'react';
import { Mail, ArrowRight, CheckCircle } from 'lucide-react';

interface NewsletterSignupProps {
    variant?: 'default' | 'sidebar';
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ variant = 'default' }) => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setStatus('loading');

        try {
            await fetch('https://hooks.zapier.com/hooks/catch/2825153/uz5cgwf/', {
                method: 'POST',
                body: JSON.stringify({ email }),
                mode: 'no-cors' // Zapier webhooks might not return CORS headers, but 'no-cors' allows the request to be sent
            });

            // With no-cors we can't check response.ok, so we assume success if no error thrown
            setStatus('success');
            setEmail('');
        } catch (error) {
            console.warn('Error submitting email:', error);
            // Even if it fails, we might want to show success to not discourage user, or add an error state. 
            // For now, let's assume success on the UI side or keep it simple.
            setStatus('success');
            setEmail('');
        }
    };

    const isSidebar = variant === 'sidebar';

    return (
        <div className={`${isSidebar ? 'my-6' : 'my-12'} bg-gradient-to-br from-indigo-900 to-indigo-800 rounded-2xl ${isSidebar ? 'p-6' : 'p-8 md:p-12'} text-white shadow-xl relative overflow-hidden`}>
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500 opacity-10 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>

            <div className={`relative z-10 flex flex-col ${isSidebar ? '' : 'md:flex-row'} items-center justify-between gap-8`}>
                <div className={`flex-1 ${isSidebar ? 'text-center' : 'text-center md:text-left'}`}>
                    <div className={`flex items-center justify-center ${isSidebar ? '' : 'md:justify-start'} gap-3 mb-4`}>
                        <div className="bg-indigo-700 p-2 rounded-lg">
                            <Mail className="w-6 h-6 text-indigo-200" />
                        </div>
                        <span className="text-indigo-200 font-medium tracking-wide text-sm uppercase">Newsletter</span>
                    </div>
                    <h3 className={`${isSidebar ? 'text-xl' : 'text-2xl md:text-3xl'} font-bold mb-3`}>
                        Domine suas finanças
                    </h3>
                    <p className="text-indigo-100 text-lg max-w-md mx-auto md:mx-0">
                        Receba dicas exclusivas de investimentos e planejamento financeiro diretamente no seu e-mail.
                    </p>
                </div>

                <div className={`w-full ${isSidebar ? '' : 'md:w-auto'} flex-1 max-w-md`}>
                    {status === 'success' ? (
                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-center animate-fade-in">
                            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                            <h4 className="text-xl font-bold mb-1">Inscrição confirmada!</h4>
                            <p className="text-indigo-200">Bem-vindo ao clube dos investidores inteligentes.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                            <div className="relative">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Seu melhor e-mail"
                                    className="w-full px-5 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-indigo-300 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                                    required
                                    aria-label="Endereço de e-mail"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={status === 'loading'}
                                className="w-full bg-white text-indigo-900 font-bold py-4 px-6 rounded-xl hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2 group"
                            >
                                {status === 'loading' ? 'Inscrevendo...' : 'Quero receber dicas'}
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <p className="text-xs text-indigo-300 text-center mt-2">
                                Sem spam. Cancele a qualquer momento.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewsletterSignup;
